<template>
    <div class="card" style="min-height: calc(100vh - 180px)">
        <PageHeader
            title="Recurring Invoice"
            subtitle="Generate service invoices from the services set on contact profiles for a date range."
            @onClickCloseButton="onClickBack"
        />

        <div class="px-2 mt-2">
            <div class="row">
                <div class="col-12 col-sm-6 col-md-3 mb-1">
                    <label class="col-form-label">Start Date</label>
                    <vField
                        v-model="startDate"
                        name="start_date"
                        type="date"
                        class="form-control"
                    />
                </div>
                <div class="col-12 col-sm-6 col-md-3 mb-1">
                    <label class="col-form-label">End Date</label>
                    <vField
                        v-model="endDate"
                        name="end_date"
                        type="date"
                        class="form-control"
                    />
                </div>
                <div class="col-12 col-sm-6 col-md-3 mb-1">
                    <label class="col-form-label">Due Date</label>
                    <vField
                        v-model="dueDate"
                        name="due_date"
                        type="datetime-local"
                        class="form-control"
                        :min="minDueDate"
                    />
                </div>
                <div class="col-12 col-sm-6 col-md-3 mb-1 d-flex align-items-end">
                    <button
                        style="min-width: 64px"
                        class="btn btn-primary waves-effect waves-float waves-light"
                        @click="onClickProcess"
                    >Process</button>
                </div>
            </div>

            <div class="table-responsive mt-2" style="min-height: 400px">
                <table class="table table-bordered">
                    <thead>
                    <tr>
                        <th class="text-center">Start Date</th>
                        <th class="text-center">End Date</th>
                        <th class="text-center">Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in tableItems" :key="item.id">
                        <td class="text-center">{{ item.start_date }}</td>
                        <td class="text-center">{{ item.end_date }}</td>
                        <td class="text-center text-capitalize">{{ item.status }}</td>
                    </tr>
                    </tbody>
                </table>
                <p v-if="! tableItems.length" class="text-center mt-5">No data</p>
            </div>
        </div>

        <div class="px-2"><Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/></div>
        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/atom/PageHeader.vue'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import handleHrPayroll from '@/services/modules/hrPayroll/hrPayroll'
import useDate from '@/services/utils/day'

const store = useStore()
const route = useRoute()
const router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')
const { endOfTodayForInput, formatForInput } = useDate()
const { createProcessTracker, fetchProcessTrackerData } = handleHrPayroll()

const loading = ref(false)
const startDate = ref(route.query.start || '')
const endDate = ref(route.query.end || '')
const dueDate = ref(endOfTodayForInput())
const minDueDate = formatForInput(new Date()) + 'T00:00'
const tableItems = ref([])
const params = ref({
    offset: 20
})

const companyQuery = () => '?company_id=' + route.params.companyId

const dueDateQuery = () => {
    if (! dueDate.value) return ''
    return '&due_date=' + dueDate.value.replace('T', ' ')
}

const setPagination = (data) => {
    store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page
    })
}

const getProcessList = async () => {
    let query = companyQuery() + '&type=bill_process&offset=' + params.value.offset
    if (route.query.page) query += '&page=' + route.query.page
    loading.value = true
    tableItems.value = []
    try {
        const res = await fetchProcessTrackerData(query)
        if (! res.status) {
            store.commit('resetPagination')
            return
        }
        tableItems.value = res.data.data
        setPagination(res.data)
    } catch (err) {
        showError('Something is wrong. Check your connectivity!!')
    } finally {
        loading.value = false
    }
}

const onClickBack = () => {
    router.push({name: 'service-sales-list', params: route.params, query: {start: route.query.start, end: route.query.end}})
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getProcessList()
}

const onClickProcess = async () => {
    if (! startDate.value || ! endDate.value) {
        showError('Start Date and End Date are required')
        return
    }
    const query = companyQuery() + '&type=bill_process&start_date=' + startDate.value + '&end_date=' + endDate.value + dueDateQuery()
    loading.value = true
    try {
        const res = await createProcessTracker(query)
        if (! res.status) {
            showError(res.message)
            return
        }
        showSuccess(res.message)
        await getProcessList()
    } catch (err) {
        showError('Something is wrong. Check your connectivity!!')
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    getProcessList()
})
</script>
